import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Power,
  PowerOff,
  Search,
  Activity,
  Clock,
  AlertCircle,
  CheckCircle,
  Loader2,
  X,
  Save,
  TestTube,
  BarChart3,
  Tag
} from 'lucide-react';
import Button from '../components/Button';
import {
  searchTemplateService,
  SearchTemplate,
  CreateSearchTemplateRequest,
  UpdateSearchTemplateRequest,
} from '../services/searchTemplateService';

const SearchTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<SearchTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SearchTemplate | null>(null);

  // Filter states
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [categories, setCategories] = useState<string[]>([]);

  // Form states
  const [formData, setFormData] = useState<CreateSearchTemplateRequest>({
    template_name: '',
    description: '',
    category: '',
    system_prompt_template: '',
    response_schema: {},
    expected_response: '',
    model_name: 'gemini-2.0-flash',
    max_tokens: 500,
    temperature: 0.3,
    timeout_seconds: 10,
    cache_enabled: true,
    cache_ttl_seconds: 3600,
    requires_auth: true,
  });

  // Test states
  const [testQuery, setTestQuery] = useState('');
  const [testResults, setTestResults] = useState<any>(null);
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      const data = await searchTemplateService.listTemplates();
      setTemplates(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch templates');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const cats = await searchTemplateService.getCategories();
      setCategories(cats);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  // Generate default expected response based on template data and response_schema
  const generateDefaultExpectedResponse = (data: CreateSearchTemplateRequest): string => {
    // Build the results based on response_schema if provided
    let exampleResults: any = [
      { name: "Example Item 1", unit: "piece" },
      { name: "Example Item 2", unit: "serving" }
    ];

    // If response_schema is provided, try to use it as a guide for the results structure
    if (data.response_schema && Object.keys(data.response_schema).length > 0) {
      // Use response_schema as the example results structure
      exampleResults = data.response_schema;
    }

    const defaultResponse = {
      success: true,
      template_id: 1,
      template_name: data.template_name || "template_name",
      category: data.category || null,
      query: "<user_query>",
      total_results: Array.isArray(exampleResults) ? exampleResults.length : 1,
      results: exampleResults,
      cached: false,
      processing_time_seconds: 1.5,
      request_id: "uuid-xxxx-xxxx-xxxx"
    };
    return JSON.stringify(defaultResponse, null, 2);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Auto-generate expected_response if not provided
      const dataToSubmit = {
        ...formData,
        expected_response: formData.expected_response || generateDefaultExpectedResponse(formData)
      };
      await searchTemplateService.createTemplate(dataToSubmit);
      setSuccess('Template created successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchTemplates();
      fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create template');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplate) return;

    try {
      const updates: UpdateSearchTemplateRequest = {
        description: formData.description,
        system_prompt_template: formData.system_prompt_template,
        response_schema: formData.response_schema,
        expected_response: formData.expected_response,
        model_name: formData.model_name,
        max_tokens: formData.max_tokens,
        temperature: formData.temperature,
        timeout_seconds: formData.timeout_seconds,
        cache_enabled: formData.cache_enabled,
        cache_ttl_seconds: formData.cache_ttl_seconds,
        requires_auth: formData.requires_auth,
      };

      await searchTemplateService.updateTemplate(selectedTemplate.id, updates);
      setSuccess('Template updated successfully!');
      setShowEditModal(false);
      setSelectedTemplate(null);
      resetForm();
      fetchTemplates();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update template');
    }
  };

  const handleDelete = async (templateId: number, templateName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${templateName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await searchTemplateService.deleteTemplate(templateId);
      setSuccess('Template deleted successfully!');
      fetchTemplates();
      fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleToggle = async (templateId: number) => {
    try {
      await searchTemplateService.toggleTemplate(templateId);
      setSuccess('Template status updated!');
      fetchTemplates();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to toggle template');
    }
  };

  const handleTest = async () => {
    if (!selectedTemplate || !testQuery) {
      setError('Please enter a test query');
      return;
    }

    try {
      setIsTesting(true);
      const results = await searchTemplateService.testSearch({
        template_id: selectedTemplate.id,
        query: testQuery,
        limit: 5,
      });
      setTestResults(results);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Test failed');
    } finally {
      setIsTesting(false);
    }
  };

  const openEditModal = (template: SearchTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      template_name: template.template_name,
      description: template.description || '',
      category: template.category || '',
      system_prompt_template: template.system_prompt_template,
      response_schema: template.response_schema || {},
      expected_response: template.expected_response || '',
      model_name: template.model_name,
      max_tokens: template.max_tokens,
      temperature: template.temperature,
      timeout_seconds: template.timeout_seconds,
      cache_enabled: template.cache_enabled,
      cache_ttl_seconds: template.cache_ttl_seconds,
      requires_auth: template.requires_auth,
    });
    setShowEditModal(true);
  };

  const openTestModal = (template: SearchTemplate) => {
    setSelectedTemplate(template);
    setTestQuery('');
    setTestResults(null);
    setShowTestModal(true);
  };

  const resetForm = () => {
    setFormData({
      template_name: '',
      description: '',
      category: '',
      system_prompt_template: '',
      response_schema: {},
      expected_response: '',
      model_name: 'gemini-2.0-flash',
      max_tokens: 500,
      temperature: 0.3,
      timeout_seconds: 10,
      cache_enabled: true,
      cache_ttl_seconds: 3600,
      requires_auth: true,
    });
  };

  const filteredTemplates = filterCategory
    ? templates.filter(t => t.category === filterCategory)
    : templates;

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            AI Search Templates
          </h1>
          <p className="text-gray-600">
            Create and manage configurable AI-powered search templates for any use case
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3" />
            <span className="text-green-800">{success}</span>
          </div>
        )}

        {/* Filters and Actions */}
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <Button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="ml-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Template
          </Button>
        </div>

        {/* Templates Grid */}
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border border-gray-200"
              >
                {/* Template Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {template.template_name}
                    </h3>
                    {template.category && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        <Tag className="h-3 w-3 mr-1" />
                        {template.category}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleToggle(template.id)}
                    className={`p-2 rounded-lg transition-colors ${
                      template.is_active
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={template.is_active ? 'Active' : 'Inactive'}
                  >
                    {template.is_active ? (
                      <Power className="h-5 w-5" />
                    ) : (
                      <PowerOff className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {/* Template Description */}
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {template.description || 'No description provided'}
                </p>

                {/* Template Stats */}
                <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-gray-200">
                  <div className="flex items-center text-sm">
                    <Activity className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="text-gray-600">
                      {template.total_requests} requests
                    </span>
                  </div>
                  <div className="flex items-center text-sm">
                    <Clock className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="text-gray-600">
                      {template.cache_enabled ? 'Cached' : 'No cache'}
                    </span>
                  </div>
                </div>

                {/* Template Model Info */}
                <div className="text-xs text-gray-500 mb-4">
                  Model: {template.model_name} | Tokens: {template.max_tokens} | Temp: {template.temperature}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openTestModal(template)}
                    className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <TestTube className="h-4 w-4 mr-1" />
                    Test
                  </button>
                  <button
                    onClick={() => openEditModal(template)}
                    className="px-3 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(template.id, template.template_name)}
                    className="px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {filteredTemplates.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <p className="text-gray-500">No templates found. Create one to get started!</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Create Search Template</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleCreate} className="p-6 space-y-6">
              {/* Template Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.template_name}
                  onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  placeholder="e.g., Medical Literature Search"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  rows={2}
                  placeholder="Brief description of what this template does"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  placeholder="e.g., Medical, Research, Legal"
                />
              </div>

              {/* System Prompt Template */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  System Prompt Template *
                </label>
                <textarea
                  required
                  value={formData.system_prompt_template}
                  onChange={(e) => setFormData({ ...formData, system_prompt_template: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white font-mono text-sm"
                  rows={4}
                  placeholder="Enter the AI system prompt. Use {query} as a placeholder for user input."
                />
              </div>

              {/* Response Schema */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Response Schema (JSON)
                </label>
                <textarea
                  value={JSON.stringify(formData.response_schema, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value || '{}');
                      setFormData({ ...formData, response_schema: parsed });
                    } catch (err) {
                      // Invalid JSON, ignore
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white font-mono text-sm"
                  rows={4}
                  placeholder='{"type": "object", "properties": {...}}'
                />
              </div>

              {/* Expected Response Example */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Response Example
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Document an example of the expected API response. This helps other developers understand the response format.
                </p>
                <textarea
                  value={formData.expected_response}
                  onChange={(e) => setFormData({ ...formData, expected_response: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white font-mono text-sm"
                  rows={8}
                  placeholder={`{
  "success": true,
  "template_name": "food_nutrients",
  "query": "protein",
  "total_results": 10,
  "results": [
    {"name": "Chicken Breast", "unit": "100g"},
    {"name": "Eggs", "unit": "piece"}
  ],
  "cached": false,
  "processing_time_seconds": 1.5
}`}
                />
              </div>

              {/* Model Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Model
                  </label>
                  <select
                    value={formData.model_name}
                    onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                    <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
                    <option value="gpt-4">GPT-4</option>
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Tokens
                  </label>
                  <input
                    type="number"
                    value={formData.max_tokens}
                    onChange={(e) => setFormData({ ...formData, max_tokens: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    min="1"
                    max="8000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Temperature ({formData.temperature})
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={formData.temperature}
                    onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timeout (seconds)
                  </label>
                  <input
                    type="number"
                    value={formData.timeout_seconds}
                    onChange={(e) => setFormData({ ...formData, timeout_seconds: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    min="1"
                    max="300"
                  />
                </div>
              </div>

              {/* Cache Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="cache_enabled"
                    checked={formData.cache_enabled}
                    onChange={(e) => setFormData({ ...formData, cache_enabled: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="cache_enabled" className="ml-2 block text-sm text-gray-700">
                    Enable Caching
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cache TTL (seconds)
                  </label>
                  <input
                    type="number"
                    value={formData.cache_ttl_seconds}
                    onChange={(e) => setFormData({ ...formData, cache_ttl_seconds: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    min="60"
                    disabled={!formData.cache_enabled}
                  />
                </div>
              </div>

              {/* Auth Required */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requires_auth"
                  checked={formData.requires_auth}
                  onChange={(e) => setFormData({ ...formData, requires_auth: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="requires_auth" className="ml-2 block text-sm text-gray-700">
                  Requires Authentication
                </label>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <Button type="submit">
                  <Save className="h-4 w-4 mr-2" />
                  Create Template
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Edit Template</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedTemplate(null);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleUpdate} className="p-6 space-y-6">
              {/* Template Name (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name
                </label>
                <input
                  type="text"
                  disabled
                  value={formData.template_name}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  rows={2}
                />
              </div>

              {/* System Prompt Template */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  System Prompt Template *
                </label>
                <textarea
                  required
                  value={formData.system_prompt_template}
                  onChange={(e) => setFormData({ ...formData, system_prompt_template: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white font-mono text-sm"
                  rows={4}
                />
              </div>

              {/* Response Schema */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Response Schema (JSON)
                </label>
                <textarea
                  value={JSON.stringify(formData.response_schema, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value || '{}');
                      setFormData({ ...formData, response_schema: parsed });
                    } catch (err) {
                      // Invalid JSON, ignore
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white font-mono text-sm"
                  rows={4}
                />
              </div>

              {/* Expected Response Example */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Response Example
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Document an example of the expected API response. This helps other developers understand the response format.
                </p>
                <textarea
                  value={formData.expected_response}
                  onChange={(e) => setFormData({ ...formData, expected_response: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white font-mono text-sm"
                  rows={8}
                  placeholder={`{
  "success": true,
  "template_name": "food_nutrients",
  "query": "protein",
  "total_results": 10,
  "results": [
    {"name": "Chicken Breast", "unit": "100g"},
    {"name": "Eggs", "unit": "piece"}
  ],
  "cached": false,
  "processing_time_seconds": 1.5
}`}
                />
              </div>

              {/* Model Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Model
                  </label>
                  <select
                    value={formData.model_name}
                    onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                    <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
                    <option value="gpt-4">GPT-4</option>
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Tokens
                  </label>
                  <input
                    type="number"
                    value={formData.max_tokens}
                    onChange={(e) => setFormData({ ...formData, max_tokens: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    min="1"
                    max="8000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Temperature ({formData.temperature})
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={formData.temperature}
                    onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timeout (seconds)
                  </label>
                  <input
                    type="number"
                    value={formData.timeout_seconds}
                    onChange={(e) => setFormData({ ...formData, timeout_seconds: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    min="1"
                    max="300"
                  />
                </div>
              </div>

              {/* Cache Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="edit_cache_enabled"
                    checked={formData.cache_enabled}
                    onChange={(e) => setFormData({ ...formData, cache_enabled: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="edit_cache_enabled" className="ml-2 block text-sm text-gray-700">
                    Enable Caching
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cache TTL (seconds)
                  </label>
                  <input
                    type="number"
                    value={formData.cache_ttl_seconds}
                    onChange={(e) => setFormData({ ...formData, cache_ttl_seconds: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    min="60"
                    disabled={!formData.cache_enabled}
                  />
                </div>
              </div>

              {/* Auth Required */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="edit_requires_auth"
                  checked={formData.requires_auth}
                  onChange={(e) => setFormData({ ...formData, requires_auth: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="edit_requires_auth" className="ml-2 block text-sm text-gray-700">
                  Requires Authentication
                </label>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedTemplate(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <Button type="submit">
                  <Save className="h-4 w-4 mr-2" />
                  Update Template
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Test Modal */}
      {showTestModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Test Template: {selectedTemplate.template_name}
              </h2>
              <button
                onClick={() => {
                  setShowTestModal(false);
                  setSelectedTemplate(null);
                  setTestQuery('');
                  setTestResults(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Test Query Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Test Query
                </label>
                <textarea
                  value={testQuery}
                  onChange={(e) => setTestQuery(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  rows={3}
                  placeholder="Enter a test query to see how the template performs..."
                />
              </div>

              {/* Test Button */}
              <div className="flex justify-end">
                <Button onClick={handleTest} disabled={isTesting || !testQuery}>
                  {isTesting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <TestTube className="h-4 w-4 mr-2" />
                      Run Test
                    </>
                  )}
                </Button>
              </div>

              {/* Test Results */}
              {testResults && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Results</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      {testResults.cached && (
                        <span className="flex items-center text-green-600">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Cached
                        </span>
                      )}
                      {testResults.processing_time_seconds && (
                        <span className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {testResults.processing_time_seconds.toFixed(2)}s
                        </span>
                      )}
                      <span className="flex items-center">
                        <BarChart3 className="h-4 w-4 mr-1" />
                        {testResults.total_results} results
                      </span>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto">
                      {JSON.stringify(testResults.results, null, 2)}
                    </pre>
                  </div>

                  <div className="text-xs text-gray-500">
                    Request ID: {testResults.request_id}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchTemplates;
